import { useEffect, useState } from "react";
import { usePosts } from "../contexts/PostContext";
import { useUser } from "../contexts/UserContext";
import BoardList from "../components/BoardList";
import BoardDetail from "../components/BoardDetail";
import BoardWritePage from "./BoardWritePage";
import { Post } from "../types/board";

const PAGE_SIZE = 20;

export default function AnonymousBoardPage() {
  const { posts, loadPosts } = usePosts();
  const { user } = useUser();
  const [selected, setSelected] = useState<Post | null>(null);
  const [showWrite, setShowWrite] = useState(false);

  // 페이지네이션 & 검색 상태
  const [page, setPage] = useState(1);
  const [searchType, setSearchType] = useState<"title" | "title+content" | "author">("title");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [filtered, setFiltered] = useState<Post[]>([]);

  useEffect(() => {
    loadPosts("익명게시판");
  }, []);

  // 검색 & 필터링
  useEffect(() => {
    let filteredPosts = posts.filter((p) => p.board_type === "익명게시판");

    if (searchKeyword.trim()) {
      const keyword = searchKeyword.trim().toLowerCase();

      if (searchType === "title") {
        filteredPosts = filteredPosts.filter((p) =>
          (p.title ?? "").toLowerCase().includes(keyword)
        );
      } else if (searchType === "title+content") {
        filteredPosts = filteredPosts.filter(
          (p) =>
            (p.title ?? "").toLowerCase().includes(keyword) ||
            (p.content ?? "").toLowerCase().includes(keyword)
        );
      } else if (searchType === "author") {
        filteredPosts = filteredPosts.filter((p) =>
          (p.nickname ?? "").toLowerCase().includes(keyword)
        );
      }
    }

    setFiltered(filteredPosts);
    setPage(1);
  }, [posts, searchType, searchKeyword]);

  // 페이지네이션
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pagedPosts = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // 페이지네이션 번호 범위 계산
  const startPage = Math.max(1, page - 2);
  const endPage = Math.min(totalPages, page + 2);

  function handleRowClick(post: Post) {
    setSelected(post);
  }

  return (
    <div className="max-w-4xl mx-auto mt-8">
      {showWrite ? (
        <BoardWritePage boardType="익명게시판" onClose={() => setShowWrite(false)} />
      ) : selected ? (
        user ? (
          // 🔥 익명게시판 — 공유링크 제거 (BoardDetail 내부에서 조건부 처리됨)
          <BoardDetail
            post={selected}
            onClose={() => setSelected(null)}
            onRecommend={() => {}}
            boardRoute="/anonymous"
            hideShare={true}     // ⭐ 익명게시판은 공유링크 제거
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-80 text-gray-500">
            <div className="text-lg font-semibold mb-4">
              이 글은 <b>로그인 회원</b>만 볼 수 있습니다.
            </div>
            <div className="text-sm mb-8">
              <span className="block">글 목록은 모두에게 공개됩니다.</span>
              <span className="block">상세 내용을 보려면 로그인해 주세요.</span>
            </div>

            <button
              onClick={() => setSelected(null)}
              className="mt-2 px-5 py-2 rounded border bg-white hover:bg-gray-50"
            >
              목록으로
            </button>
          </div>
        )
      ) : (
        <>
          {/* 헤더 */}
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xl font-bold">Anon Talk</h2>
            {user ? (
              <button
                className="px-3 py-1 rounded bg-black text-white"
                onClick={() => setShowWrite(true)}
              >
                글쓰기
              </button>
            ) : (
              <div className="text-gray-400 text-sm">로그인 후 글쓰기 가능</div>
            )}
          </div>

          {/* 검색 */}
          <div className="mb-4 flex gap-2">
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value as any)}
              className="border rounded px-2"
            >
              <option value="title">제목</option>
              <option value="title+content">제목+내용</option>
              <option value="author">작성자</option>
            </select>

            <input
              className="border rounded px-2 py-1 flex-1"
              placeholder="검색어를 입력하세요"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setPage(1);
              }}
            />

            <button
              className="px-3 py-1 rounded bg-gray-200"
              onClick={() => setPage(1)}
            >
              검색
            </button>
          </div>

          {/* 게시글 목록 (⭐ Free Talk과 동일한 props 전달) */}
          <BoardList
            posts={pagedPosts}
            allPosts={filtered}
            page={page}
            pageSize={PAGE_SIZE}
            onRowClick={handleRowClick}
          />

          {/* ⭐ 페이지 1개여도 항상 표시하는 새 페이지네이션 */}
          {totalPages >= 1 && (
            <div className="flex justify-center items-center gap-2 mt-10 mb-10 text-sm select-none">

              {/* First */}
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className={`px-2 py-1 rounded ${
                  page === 1 ? "text-gray-300" : "hover:bg-gray-100"
                }`}
              >
                «
              </button>

              {/* Prev */}
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className={`px-2 py-1 rounded ${
                  page === 1 ? "text-gray-300" : "hover:bg-gray-100"
                }`}
              >
                ‹
              </button>

              {/* 번호 목록 */}
              {Array.from(
                { length: Math.max(1, endPage - startPage + 1) },
                (_, i) => startPage + i
              ).map((num) => (
                <button
                  key={num}
                  onClick={() => setPage(num)}
                  className={`px-3 py-1 rounded ${
                    page === num
                      ? "bg-black text-white"
                      : "hover:bg-gray-100 text-gray-700"
                  }`}
                >
                  {num}
                </button>
              ))}

              {/* Next */}
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className={`px-2 py-1 rounded ${
                  page === totalPages ? "text-gray-300" : "hover:bg-gray-100"
                }`}
              >
                ›
              </button>

              {/* Last */}
              <button
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                className={`px-2 py-1 rounded ${
                  page === totalPages ? "text-gray-300" : "hover:bg-gray-100"
                }`}
              >
                »
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
